/**
 * In-memory sink for performance records (INP, CLS, LCP, tracked interactions).
 *
 * These metrics go straight to telemetry, so while developing there is no way to read one without
 * waiting for it to reach Kusto. This keeps the most recent in a ring buffer for the dev tools
 * "Perf Insights" panel.
 *
 * Off unless dev tools turn it on, so production retains nothing.
 */ /** A single captured performance record. */ /** Deep enough for a boot burst plus a session of interactions, shallow enough to stay bounded. */ const CAPACITY = 200;
let enabled = false;
let nextId = 1;
let records = [];
const listeners = new Set();
function publish() {
    // The panel is the only subscriber and it is only mounted while its tab is open, so usually
    // nobody is listening - and the snapshot below copies the whole buffer, on a path that runs
    // for every metric.
    if (listeners.size === 0) {
        return;
    }
    const snapshot = getRecords();
    for (const listener of listeners){
        // A broken listener must not take out the others, or reach the telemetry path that called us.
        try {
            listener(snapshot);
        } catch  {
        // ignored by design
        }
    }
}
/** Start retaining records. Only dev tools call this, which is what keeps production free. */ export function enablePerfInsights() {
    enabled = true;
}
/** Stop retaining records and drop what is held. */ export function disablePerfInsights() {
    enabled = false;
    if (records.length > 0) {
        records = [];
        publish();
    }
}
/** Safe to call from any telemetry path: does nothing unless dev tools enabled the sink. */ export function recordPerfInsight(tag, data) {
    if (!enabled) {
        return;
    }
    records.push(// Frozen here rather than in getRecords, which runs on every publish and would then copy
    // the whole buffer per metric. data is copied first - the caller shares that object with
    // the telemetry pipeline, which still needs to write to it.
    Object.freeze({
        id: nextId++,
        tag,
        wallClock: Date.now(),
        data: Object.freeze({
            ...data
        })
    }));
    if (records.length > CAPACITY) {
        records.shift();
    }
    publish();
}
/** Oldest first. The array is a copy and the records are frozen, so callers cannot disturb it. */ export function getRecords() {
    return records.slice();
}
export function clearPerfInsights() {
    if (records.length > 0) {
        records = [];
        publish();
    }
}
/** Returns its own unsubscribe, so a React effect can return it as cleanup. */ export function subscribeToPerfInsights(listener) {
    listeners.add(listener);
    return ()=>{
        listeners.delete(listener);
    };
}
